import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { SvgIconProps } from '@mui/material';
import { createSvgIcon } from '@mui/material';

import type { ConfigurationIconOptions } from '@/modules/common/types/configuration';

import * as commonSelectors from '@/modules/common/redux/selectors/common';

type Props = {
  iconKey?: string;
  className?: string;
  color?: string;
  fallback: React.ElementType<SvgIconProps>;
} & SvgIconProps;

const iconCache = new Map<string, React.ComponentType<SvgIconProps>>();

function ConfigurationIcon({ iconKey, fallback: FallbackIcon, className = '', color, ...props }: Props) {
  const configuration = useSelector(commonSelectors.configurationSelector);

  const Icon = useMemo(() => {
    if (!iconKey) {
      return undefined;
    }

    const pathOptions = configuration.icons[iconKey] as ConfigurationIconOptions | undefined;

    if (!pathOptions?.d) {
      return undefined;
    }

    if (!iconCache.has(iconKey)) {
      iconCache.set(
        iconKey,
        createSvgIcon(
          <path d={pathOptions.d} clipRule={pathOptions.clipRule} fillRule={pathOptions.fillRule} />,
          `ConfigurationIcon${iconKey}`,
        ),
      );
    }

    return iconCache.get(iconKey) ?? undefined;
  }, [configuration.icons, iconKey]);

  const ResultIcon = Icon ?? FallbackIcon;

  return <ResultIcon {...props} htmlColor={color ?? 'inherit'} className={className} />;
}

export default ConfigurationIcon;
